import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API_URL } from '../config/api';
import SkillTreeRPG from '../components/SkillTreeRPG';
import AchievementsBadges from '../components/AchievementsBadges';
import CodeBattle from '../components/CodeBattle';
import DebuggingTimeMachine from '../components/DebuggingTimeMachine';
import DailyChallenges from '../components/DailyChallenges';
import Leaderboard from '../components/Leaderboard';
import InterviewHistory from '../components/InterviewHistory';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [questionPackages, setQuestionPackages] = useState([]);
  const [interviewCount, setInterviewCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  
  // New feature states
  const [userStats, setUserStats] = useState(null);
  const [showCodeBattle, setShowCodeBattle] = useState(false);
  const [showTimeMachine, setShowTimeMachine] = useState(false);
  const [showDailyChallenge, setShowDailyChallenge] = useState(false);
  const [installmentPayingCourseId, setInstallmentPayingCourseId] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user) {
      loadDashboardData();
      loadUserStats();
      loadInterviewCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Prevent background scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen = showCodeBattle || 
                           showTimeMachine || showDailyChallenge || showLeaderboard;
    
    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCodeBattle, showTimeMachine, showDailyChallenge, showLeaderboard]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/user-dashboard/overview', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDashboardData(response.data);
    } catch (error) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', error);
    }
    
    setLoading(false);
  };

  const loadArticles = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/user-dashboard/articles', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setArticles(response.data.articles);
    } catch (error) {
      setError('Failed to load articles');
      console.error('Articles error:', error);
    }
    
    setLoading(false);
  };

  const loadCourses = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/user-dashboard/courses', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCourses(response.data.enrollments);
    } catch (error) {
      setError('Failed to load courses');
      console.error('Courses error:', error);
    }
    
    setLoading(false);
  };

  const handlePayNextCourseInstallment = async (courseId) => {
    setInstallmentPayingCourseId(courseId);
    try {
      const orderResponse = await fetch(`${API_URL}/api/courses/${courseId}/payment/create-next-installment-order`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (!orderResponse.ok) {
        const err = await orderResponse.json().catch(() => ({}));
        alert(err.error || 'Could not start payment.');
        setInstallmentPayingCourseId(null);
        return;
      }
      const orderData = await orderResponse.json();
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'YCode Universe',
        description: orderData.courseName,
        order_id: orderData.orderId,
        prefill: {
          name: localStorage.getItem('username') || '',
          email: localStorage.getItem('email') || ''
        },
        theme: { color: '#8b5cf6' },
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${API_URL}/api/courses/${courseId}/payment/verify`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature
              })
            });
            if (verifyResponse.ok) {
              await loadCourses();
            } else {
              const errorData = await verifyResponse.json().catch(() => ({}));
              alert(errorData.error || 'Payment verification failed.');
            }
          } catch (e) {
            console.error(e);
            alert('Payment verification error.');
          } finally {
            setInstallmentPayingCourseId(null);
          }
        },
        modal: {
          ondismiss: () => setInstallmentPayingCourseId(null)
        }
      };
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (e) {
      console.error(e);
      alert('Failed to start payment.');
      setInstallmentPayingCourseId(null);
    }
  };

  const loadQuestionPackages = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/user-dashboard/question-packages', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setQuestionPackages(response.data.enrollments);
    } catch (error) {
      setError('Failed to load question packages');
      console.error('Question packages error:', error);
    }
    
    setLoading(false);
  };

  const loadUserStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/gamification/stats/${user.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUserStats(response.data);
    } catch (error) {
      console.log('User stats not available yet:', error);
      // Create default stats if they don't exist
      setUserStats({
        level: 1,
        xp: 0,
        totalXp: 0,
        xpToNextLevel: 100,
        skillPoints: 0
      });
    }
  };

  const loadInterviewCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/ai-assistant/interview/history', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setInterviewCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to load interview count:', error);
      setInterviewCount(0);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    
    switch (tab) {
      case 'overview':
        if (!dashboardData) loadDashboardData();
        break;
      case 'articles':
        if (articles.length === 0) loadArticles();
        break;
      case 'courses':
        if (courses.length === 0) loadCourses();
        break;
      case 'question-packages':
        if (questionPackages.length === 0) loadQuestionPackages();
        break;
      default:
        break;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEnrollmentStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center pt-20">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔒</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">Please log in to access your dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" style={{ paddingTop: isMobile ? '60px' : '80px' }}>
      {/* Professional Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto" style={{ padding: isMobile ? '12px' : '0 16px' }}>
          <div className={`flex ${isMobile ? 'flex-col' : 'justify-between'} items-${isMobile ? 'start' : 'center'}`} style={{ padding: isMobile ? '16px 0' : '32px 0', gap: isMobile ? '16px' : 0 }}>
            <div className="flex items-center" style={{ gap: isMobile ? '12px' : '16px' }}>
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center" style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px' }}>
                <span className="text-white font-bold" style={{ fontSize: isMobile ? '18px' : '20px' }}>📊</span>
              </div>
              <div>
                <h1 className="font-bold text-gray-900" style={{ fontSize: isMobile ? '20px' : '30px' }}>Dashboard</h1>
                <p className="text-gray-600" style={{ fontSize: isMobile ? '12px' : '14px', marginTop: isMobile ? '2px' : '4px' }}>Welcome back, {user.firstName || user.username}!</p>
              </div>
            </div>
            <div className={`flex items-center ${isMobile ? 'w-full justify-between' : ''}`} style={{ gap: isMobile ? '8px' : '24px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              {userStats && (
                <>
                  <div className="text-right" style={{ flex: isMobile ? 1 : 'none' }}>
                    <p className="font-medium text-gray-500 uppercase tracking-wide" style={{ fontSize: isMobile ? '10px' : '12px' }}>Level</p>
                    <p className="font-semibold text-indigo-600" style={{ fontSize: isMobile ? '14px' : '18px' }}>⚡ {userStats.level}</p>
                  </div>
                  {!isMobile && <div className="w-px h-12 bg-gray-300"></div>}
                  <div className="text-right" style={{ flex: isMobile ? 1 : 'none' }}>
                    <p className="font-medium text-gray-500 uppercase tracking-wide" style={{ fontSize: isMobile ? '10px' : '12px' }}>XP</p>
                    <p className="font-semibold text-purple-600" style={{ fontSize: isMobile ? '12px' : '18px' }}>
                      🌟 {userStats.currentLevelExperience || userStats.experiencePoints}/{userStats.experienceToNextLevel || (userStats.experiencePoints + userStats.xpToNextLevel)}
                    </p>
                  </div>
                  {!isMobile && <div className="w-px h-12 bg-gray-300"></div>}
                </>
              )}
              <div className="text-right" style={{ flex: isMobile ? 1 : 'none' }}>
                <p className="font-medium text-gray-500 uppercase tracking-wide" style={{ fontSize: isMobile ? '10px' : '12px' }}>Member Since</p>
                <p className="font-semibold text-gray-900" style={{ fontSize: isMobile ? '12px' : '18px' }}>
                  {new Date(user.createdAt || Date.now()).toLocaleDateString('en-US', { 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Navigation */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto" style={{ padding: isMobile ? '0' : '0 16px' }}>
          <nav className="flex" style={{ 
            gap: isMobile ? '0' : '32px',
            overflowX: isMobile ? 'auto' : 'visible',
            WebkitOverflowScrolling: 'touch',
            padding: isMobile ? '0' : '0'
          }}>
            {[
              { id: 'overview', label: 'Overview', icon: '📊', color: 'blue' },
              { id: 'gamification', label: 'My Progress', icon: '🎮', color: 'indigo' },
              { id: 'interviews', label: 'AI Interviews', icon: '🎤', color: 'purple' },
              { id: 'articles', label: 'Articles', icon: '📝', color: 'green' },
              { id: 'courses', label: 'Courses', icon: '🎓', color: 'purple' },
              { id: 'question-packages', label: isMobile ? 'Questions' : 'Question Packages', icon: '❓', color: 'orange' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`group border-b-2 font-medium transition-all duration-200 flex items-center ${
                  activeTab === tab.id
                    ? `border-${tab.color}-500 text-${tab.color}-600`
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={{
                  padding: isMobile ? '12px 16px' : '16px 4px',
                  fontSize: isMobile ? '13px' : '14px',
                  minHeight: isMobile ? '48px' : 'auto',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <span className={`transition-transform duration-200 ${
                  activeTab === tab.id ? 'scale-110' : 'group-hover:scale-105'
                }`} style={{ fontSize: isMobile ? '16px' : '18px', marginRight: isMobile ? '6px' : '8px' }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-8" style={{ padding: isMobile ? '16px' : '32px', paddingLeft: isMobile ? '12px' : '16px', paddingRight: isMobile ? '12px' : '16px' }}>
        {error && (
          <div className="mb-8 bg-red-50 border-l-4 border-red-400 rounded-lg p-4 shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400 text-xl">⚠️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Loading...</p>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && !loading && (
          <div style={{ gap: isMobile ? '16px' : '32px' }} className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: isMobile ? '12px' : '24px' }}>
              {/* Articles Stats */}
              <div 
                onClick={() => handleTabChange('articles')}
                className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 cursor-pointer hover:border-green-300"
                style={{ 
                  padding: isMobile ? '16px' : '24px',
                  transform: isMobile ? 'none' : 'hover:scale-105'
                }}
              >
                <div className="flex items-center justify-between">
                  <div style={{ flex: 1 }}>
                    <p className="font-medium text-gray-600 uppercase tracking-wide" style={{ fontSize: isMobile ? '11px' : '12px' }}>Articles</p>
                    <p className="font-bold text-gray-900" style={{ fontSize: isMobile ? '24px' : '30px', marginTop: isMobile ? '4px' : '8px' }}>{dashboardData?.articles?.total || 0}</p>
                    <div className="flex" style={{ gap: isMobile ? '8px' : '16px', marginTop: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
                      <span className="text-green-600 font-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>{dashboardData?.articles?.approved || 0} approved</span>
                      <span className="text-yellow-600 font-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>{dashboardData?.articles?.pending || 0} pending</span>
                    </div>
                  </div>
                  <div className="bg-green-100 rounded-lg flex items-center justify-center" style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px', flexShrink: 0 }}>
                    <span className="text-green-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>📝</span>
                  </div>
                </div>
              </div>

              {/* Courses Stats */}
              <div 
                onClick={() => handleTabChange('courses')}
                className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 cursor-pointer hover:border-blue-300"
                style={{ 
                  padding: isMobile ? '16px' : '24px',
                  transform: isMobile ? 'none' : 'hover:scale-105'
                }}
              >
                <div className="flex items-center justify-between">
                  <div style={{ flex: 1 }}>
                    <p className="font-medium text-gray-600 uppercase tracking-wide" style={{ fontSize: isMobile ? '11px' : '12px' }}>Courses</p>
                    <p className="font-bold text-gray-900" style={{ fontSize: isMobile ? '24px' : '30px', marginTop: isMobile ? '4px' : '8px' }}>{dashboardData?.courses?.totalEnrollments || 0}</p>
                    <div className="flex" style={{ gap: isMobile ? '8px' : '16px', marginTop: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
                      <span className="text-blue-600 font-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>{dashboardData?.courses?.activeEnrollments || 0} active</span>
                      <span className="text-green-600 font-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>{dashboardData?.courses?.completedEnrollments || 0} completed</span>
                    </div>
                  </div>
                  <div className="bg-blue-100 rounded-lg flex items-center justify-center" style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px', flexShrink: 0 }}>
                    <span className="text-blue-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>🎓</span>
                  </div>
                </div>
              </div>

              {/* Question Packages Stats */}
              <div 
                onClick={() => handleTabChange('question-packages')}
                className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 cursor-pointer hover:border-purple-300"
                style={{ 
                  padding: isMobile ? '16px' : '24px',
                  transform: isMobile ? 'none' : 'hover:scale-105'
                }}
              >
                <div className="flex items-center justify-between">
                  <div style={{ flex: 1 }}>
                    <p className="font-medium text-gray-600 uppercase tracking-wide" style={{ fontSize: isMobile ? '11px' : '12px' }}>{isMobile ? 'Questions' : 'Question Packages'}</p>
                    <p className="font-bold text-gray-900" style={{ fontSize: isMobile ? '24px' : '30px', marginTop: isMobile ? '4px' : '8px' }}>{dashboardData?.questionPackages?.totalEnrollments || 0}</p>
                    <div className="flex" style={{ gap: isMobile ? '8px' : '16px', marginTop: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
                      <span className="text-purple-600 font-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>{dashboardData?.questionPackages?.activeEnrollments || 0} active</span>
                      <span className="text-green-600 font-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>{dashboardData?.questionPackages?.completedEnrollments || 0} completed</span>
                    </div>
                  </div>
                  <div className="bg-purple-100 rounded-lg flex items-center justify-center" style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px', flexShrink: 0 }}>
                    <span className="text-purple-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>❓</span>
                  </div>
                </div>
              </div>

              {/* AI Interviews Stats */}
              <div 
                onClick={() => handleTabChange('interviews')}
                className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 cursor-pointer hover:border-purple-300"
                style={{ 
                  padding: isMobile ? '16px' : '24px',
                  transform: isMobile ? 'none' : 'hover:scale-105'
                }}
              >
                <div className="flex items-center justify-between">
                  <div style={{ flex: 1 }}>
                    <p className="font-medium text-gray-600 uppercase tracking-wide" style={{ fontSize: isMobile ? '11px' : '12px' }}>AI Interviews</p>
                    <p className="font-bold text-gray-900" style={{ fontSize: isMobile ? '24px' : '30px', marginTop: isMobile ? '4px' : '8px' }}>{interviewCount}</p>
                    <div className="flex" style={{ gap: isMobile ? '8px' : '16px', marginTop: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
                      <span className="text-purple-600 font-medium" style={{ fontSize: isMobile ? '12px' : '14px' }}>Completed</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center" style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px', flexShrink: 0 }}>
                    <span className="text-purple-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>🎤</span>
                  </div>
                </div>
              </div>

            </div>

            {/* Recent Activity */}
            {(dashboardData.recentCourses?.length > 0 || dashboardData.recentPackages?.length > 0) && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200" style={{ padding: isMobile ? '16px' : '24px' }}>
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center" style={{ fontSize: isMobile ? '16px' : '18px' }}>
                  <span style={{ marginRight: isMobile ? '8px' : '8px' }}>🕒</span> Continue Learning
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: isMobile ? '12px' : '16px' }}>
                  {/* Recent Courses */}
                  {dashboardData.recentCourses?.slice(0, 2).map((enrollment) => (
                    <div 
                      key={enrollment.id}
                      onClick={() => navigate(`/dashboard/courses/${enrollment.course.id}`)}
                      className="border-2 border-blue-100 bg-blue-50 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                      style={{ padding: isMobile ? '12px' : '16px' }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1" style={{ minWidth: 0 }}>
                          <p className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors break-words" style={{ fontSize: isMobile ? '14px' : '16px' }}>{enrollment.course?.title}</p>
                          <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px', marginTop: isMobile ? '4px' : '4px' }}>
                            Last accessed: {new Date(enrollment.lastAccessedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="text-blue-600 flex-shrink-0" style={{ fontSize: isMobile ? '18px' : '20px', marginLeft: isMobile ? '8px' : '8px' }}>🎓</span>
                      </div>
                      <div style={{ marginTop: isMobile ? '12px' : '12px' }}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Progress</span>
                          <span className="font-semibold text-blue-700" style={{ fontSize: isMobile ? '11px' : '12px' }}>{enrollment.progressPercentage?.toFixed(0) || 0}%</span>
                        </div>
                        <div className="bg-gray-200 rounded-full" style={{ height: isMobile ? '6px' : '8px' }}>
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                            style={{ width: `${enrollment.progressPercentage || 0}%`, height: isMobile ? '6px' : '8px' }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Recent Packages */}
                  {dashboardData.recentPackages?.slice(0, 2).map((enrollment) => (
                    <div 
                      key={enrollment.id}
                      onClick={() => navigate(`/question-packages/${enrollment.questionPackage.id}`)}
                      className="border-2 border-purple-100 bg-purple-50 rounded-lg hover:border-purple-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                      style={{ padding: isMobile ? '12px' : '16px' }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1" style={{ minWidth: 0 }}>
                          <p className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors break-words" style={{ fontSize: isMobile ? '14px' : '16px' }}>{enrollment.questionPackage?.name}</p>
                          <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px', marginTop: isMobile ? '4px' : '4px' }}>
                            Last accessed: {new Date(enrollment.lastAccessedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="text-purple-600 flex-shrink-0" style={{ fontSize: isMobile ? '18px' : '20px', marginLeft: isMobile ? '8px' : '8px' }}>❓</span>
                      </div>
                      <div style={{ marginTop: isMobile ? '12px' : '12px', fontSize: isMobile ? '11px' : '12px' }} className="flex items-center justify-between">
                        <span className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>{enrollment.questionsAttempted || 0}/{enrollment.questionPackage?.totalQuestions || 0} questions</span>
                        <span className="font-semibold text-purple-700" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                          {enrollment.questionsAttempted > 0 
                            ? `${((enrollment.questionsCorrect / enrollment.questionsAttempted) * 100).toFixed(0)}% correct` 
                            : 'Not started'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200" style={{ padding: isMobile ? '16px' : '24px' }}>
              <h3 className="font-semibold text-gray-900 mb-4" style={{ fontSize: isMobile ? '16px' : '18px' }}>Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: isMobile ? '12px' : '16px' }}>
                <button 
                  onClick={() => navigate('/articles')}
                  className="bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-left w-full"
                  style={{ padding: isMobile ? '12px' : '16px', minHeight: isMobile ? '64px' : 'auto' }}
                >
                  <div className="flex items-center" style={{ gap: isMobile ? '10px' : '12px' }}>
                    <span className="text-blue-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>📝</span>
                    <div>
                      <p className="font-medium text-gray-900" style={{ fontSize: isMobile ? '13px' : '14px' }}>Browse Articles</p>
                      <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Read and explore articles</p>
                    </div>
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/courses')}
                  className="bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200 text-left w-full"
                  style={{ padding: isMobile ? '12px' : '16px', minHeight: isMobile ? '64px' : 'auto' }}
                >
                  <div className="flex items-center" style={{ gap: isMobile ? '10px' : '12px' }}>
                    <span className="text-green-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>🎓</span>
                    <div>
                      <p className="font-medium text-gray-900" style={{ fontSize: isMobile ? '13px' : '14px' }}>Browse Courses</p>
                      <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Start learning</p>
                    </div>
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/question-packages')}
                  className="bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors duration-200 text-left w-full"
                  style={{ padding: isMobile ? '12px' : '16px', minHeight: isMobile ? '64px' : 'auto' }}
                >
                  <div className="flex items-center" style={{ gap: isMobile ? '10px' : '12px' }}>
                    <span className="text-purple-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>❓</span>
                    <div>
                      <p className="font-medium text-gray-900" style={{ fontSize: isMobile ? '13px' : '14px' }}>Practice Questions</p>
                      <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Test your skills</p>
                    </div>
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/interview-simulator')}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg hover:from-purple-100 hover:to-pink-100 transition-all duration-200 text-left w-full border-2 border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md"
                  style={{ padding: isMobile ? '12px' : '16px', minHeight: isMobile ? '64px' : 'auto' }}
                >
                  <div className="flex items-center" style={{ gap: isMobile ? '10px' : '12px' }}>
                    <span className="text-purple-600" style={{ fontSize: isMobile ? '18px' : '20px' }}>🎤</span>
                    <div>
                      <p className="font-medium text-gray-900 flex items-center gap-1" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                        AI Interview
                        <span className="bg-purple-600 text-white rounded-full" style={{ fontSize: isMobile ? '9px' : '10px', padding: isMobile ? '2px 6px' : '2px 8px' }}>NEW</span>
                      </p>
                      <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px' }}>Practice mock interviews</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Gamification Tab */}
        {activeTab === 'gamification' && !loading && (
          <div style={{ gap: isMobile ? '16px' : '32px' }} className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="font-bold text-gray-900 flex items-center" style={{ fontSize: isMobile ? '20px' : '30px' }}>
                <span style={{ marginRight: isMobile ? '8px' : '12px' }}>🎮</span> Your Progress & Achievements
              </h2>
            </div>
            
            {/* Skill Tree and Stats */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200" style={{ padding: isMobile ? '16px' : '24px' }}>
              <h3 className="font-semibold text-gray-900 mb-6 flex items-center" style={{ fontSize: isMobile ? '18px' : '24px' }}>
                <span style={{ marginRight: isMobile ? '8px' : '8px' }}>🌳</span> RPG Skill Tree
              </h3>
              {userStats && userStats.level === 1 && userStats.experiencePoints === 0 ? (
                <div className="text-center" style={{ padding: isMobile ? '24px 0' : '48px 0' }}>
                  <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: isMobile ? '16px' : '16px' }}>🌱</div>
                  <h4 className="font-semibold text-gray-900 mb-2" style={{ fontSize: isMobile ? '18px' : '20px' }}>Begin Your Journey!</h4>
                  <p className="text-gray-600 mb-6 mx-auto" style={{ fontSize: isMobile ? '13px' : '14px', maxWidth: isMobile ? '100%' : '512px', padding: isMobile ? '0 16px' : 0 }}>
                    Start solving problems to earn XP and unlock your skill tree. Each problem you solve brings you closer to leveling up!
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 mx-auto" style={{ gap: isMobile ? '12px' : '16px', maxWidth: isMobile ? '100%' : '672px' }}>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200" style={{ padding: isMobile ? '12px' : '16px' }}>
                      <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: isMobile ? '8px' : '8px' }}>⚡</div>
                      <p className="font-semibold text-gray-900" style={{ fontSize: isMobile ? '13px' : '14px' }}>Earn XP</p>
                      <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px', marginTop: isMobile ? '4px' : '4px' }}>Solve problems to gain experience points</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200" style={{ padding: isMobile ? '12px' : '16px' }}>
                      <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: isMobile ? '8px' : '8px' }}>📈</div>
                      <p className="font-semibold text-gray-900" style={{ fontSize: isMobile ? '13px' : '14px' }}>Level Up</p>
                      <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px', marginTop: isMobile ? '4px' : '4px' }}>Gain levels and unlock skill points</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200" style={{ padding: isMobile ? '12px' : '16px' }}>
                      <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: isMobile ? '8px' : '8px' }}>🎯</div>
                      <p className="font-semibold text-gray-900" style={{ fontSize: isMobile ? '13px' : '14px' }}>Master Skills</p>
                      <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '12px', marginTop: isMobile ? '4px' : '4px' }}>Invest points in different skill trees</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/question-packages')}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{ 
                      marginTop: isMobile ? '20px' : '24px',
                      padding: isMobile ? '12px 20px' : '12px 24px',
                      fontSize: isMobile ? '14px' : '16px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    {isMobile ? 'Start Solving →' : 'Start Solving Problems →'}
                  </button>
                </div>
              ) : (
                user && <SkillTreeRPG inline={true} />
              )}
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200" style={{ padding: isMobile ? '16px' : '24px' }}>
              <h3 className="font-semibold text-gray-900 mb-6 flex items-center" style={{ fontSize: isMobile ? '18px' : '24px' }}>
                <span style={{ marginRight: isMobile ? '8px' : '8px' }}>🏆</span> Achievements & Badges
              </h3>
              {userStats && userStats.level === 1 && userStats.experiencePoints === 0 ? (
                <div className="text-center" style={{ padding: isMobile ? '24px 0' : '48px 0' }}>
                  <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: isMobile ? '16px' : '16px' }}>🎖️</div>
                  <h4 className="font-semibold text-gray-900 mb-2" style={{ fontSize: isMobile ? '18px' : '20px' }}>Unlock Your First Achievement!</h4>
                  <p className="text-gray-600 mb-6 mx-auto" style={{ fontSize: isMobile ? '13px' : '14px', maxWidth: isMobile ? '100%' : '512px', padding: isMobile ? '0 16px' : 0 }}>
                    Achievements are waiting to be unlocked! Complete challenges and milestones to earn rare badges and bonus rewards.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 mx-auto mb-6" style={{ gap: isMobile ? '8px' : '12px', maxWidth: isMobile ? '100%' : '768px' }}>
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200" style={{ padding: isMobile ? '10px' : '12px' }}>
                      <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: isMobile ? '4px' : '4px' }}>🎯</div>
                      <p className="font-semibold text-gray-700" style={{ fontSize: isMobile ? '11px' : '12px' }}>First Steps</p>
                      <p className="text-gray-500" style={{ fontSize: isMobile ? '10px' : '12px' }}>Solve 1 problem</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200" style={{ padding: isMobile ? '10px' : '12px' }}>
                      <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: isMobile ? '4px' : '4px' }}>🌟</div>
                      <p className="font-semibold text-gray-700" style={{ fontSize: isMobile ? '11px' : '12px' }}>Getting Started</p>
                      <p className="text-gray-500" style={{ fontSize: isMobile ? '10px' : '12px' }}>Solve 10 problems</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200" style={{ padding: isMobile ? '10px' : '12px' }}>
                      <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: isMobile ? '4px' : '4px' }}>✨</div>
                      <p className="font-semibold text-gray-700" style={{ fontSize: isMobile ? '11px' : '12px' }}>Perfectionist</p>
                      <p className="text-gray-500" style={{ fontSize: isMobile ? '10px' : '12px' }}>95+ code quality</p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200" style={{ padding: isMobile ? '10px' : '12px' }}>
                      <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: isMobile ? '4px' : '4px' }}>🔥</div>
                      <p className="font-semibold text-gray-700" style={{ fontSize: isMobile ? '11px' : '12px' }}>Consistent</p>
                      <p className="text-gray-500" style={{ fontSize: isMobile ? '10px' : '12px' }}>7-day streak</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/question-packages')}
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Begin Your Quest →
                  </button>
                </div>
              ) : (
                user && <AchievementsBadges inline={true} />
              )}
            </div>

            {/* Quick Access to Features - COMMENTED OUT FOR NOW, WILL IMPLEMENT LATER */}
            {/* <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl shadow-lg p-6 border-2 border-purple-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⚡</span> Try All Innovative Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button 
                  onClick={() => setShowDailyChallenge(true)}
                  className="p-6 bg-white rounded-lg hover:shadow-xl transition-all duration-200 text-center border-2 border-purple-200 hover:border-purple-400 transform hover:scale-105"
                >
                  <div className="text-4xl mb-3">📅</div>
                  <p className="font-bold text-gray-900 mb-1">Daily Challenge</p>
                  <p className="text-sm text-gray-600">Complete daily tasks and build streaks</p>
                </button>
                <button 
                  onClick={() => setShowLeaderboard(true)}
                  className="p-6 bg-white rounded-lg hover:shadow-xl transition-all duration-200 text-center border-2 border-amber-200 hover:border-amber-400 transform hover:scale-105"
                >
                  <div className="text-4xl mb-3">🏆</div>
                  <p className="font-bold text-gray-900 mb-1">Leaderboard</p>
                  <p className="text-sm text-gray-600">Compete globally and climb ranks</p>
                </button>
                <button 
                  onClick={() => setShowCodeBattle(true)}
                  className="p-6 bg-white rounded-lg hover:shadow-xl transition-all duration-200 text-center border-2 border-red-200 hover:border-red-400 transform hover:scale-105"
                >
                  <div className="text-4xl mb-3">⚔️</div>
                  <p className="font-bold text-gray-900 mb-1">Code Battle Royale</p>
                  <p className="text-sm text-gray-600">Compete in real-time coding competitions</p>
                </button>
                <button 
                  onClick={() => setShowTimeMachine(true)}
                  className="p-6 bg-white rounded-lg hover:shadow-xl transition-all duration-200 text-center border-2 border-blue-200 hover:border-blue-400 transform hover:scale-105"
                >
                  <div className="text-4xl mb-3">⏰</div>
                  <p className="font-bold text-gray-900 mb-1">Debugging Time Machine</p>
                  <p className="text-sm text-gray-600">Travel through code history to find bugs</p>
                </button>
              </div>
            </div> */}
          </div>
        )}

        {/* AI Interviews Tab */}
        {activeTab === 'interviews' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
                <span className="text-2xl sm:text-3xl">🎤</span>
                <span className="text-lg sm:text-2xl">Your AI Interview History</span>
              </h2>
              <button 
                onClick={() => navigate('/interview-simulator')}
                className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg min-h-[44px] text-sm sm:text-base"
              >
                Start New Interview
              </button>
            </div>
            
            <InterviewHistory />
          </div>
        )}

        {/* Articles Tab */}
        {activeTab === 'articles' && !loading && (
          <div style={{ gap: isMobile ? '16px' : '24px' }} className="space-y-6">
            <div className={`flex ${isMobile ? 'flex-col' : 'justify-between'} items-${isMobile ? 'start' : 'center'}`} style={{ gap: isMobile ? '12px' : 0 }}>
              <h2 className="font-bold text-gray-900" style={{ fontSize: isMobile ? '20px' : '24px' }}>Your Articles</h2>
              <button 
                onClick={() => navigate('/articles')}
                className="bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                style={{ 
                  padding: isMobile ? '10px 16px' : '8px 16px',
                  fontSize: isMobile ? '14px' : '14px',
                  minHeight: isMobile ? '44px' : 'auto',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Browse Articles
              </button>
            </div>
            
            {articles.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg text-center border border-gray-200" style={{ padding: isMobile ? '32px 16px' : '48px' }}>
                <div className="bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" style={{ width: isMobile ? '64px' : '64px', height: isMobile ? '64px' : '64px' }}>
                  <span style={{ fontSize: isMobile ? '32px' : '32px' }}>📝</span>
                </div>
                <h3 className="font-medium text-gray-900 mb-2" style={{ fontSize: isMobile ? '16px' : '18px' }}>No articles yet</h3>
                <p className="text-gray-600 mb-6" style={{ fontSize: isMobile ? '13px' : '14px' }}>Browse articles to get inspired and start your learning journey.</p>
                <button 
                  onClick={() => navigate('/articles')}
                  className="bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                  style={{ 
                    padding: isMobile ? '12px 24px' : '12px 24px',
                    fontSize: isMobile ? '14px' : '14px',
                    minHeight: isMobile ? '44px' : 'auto'
                  }}
                >
                  Browse Articles
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: isMobile ? '12px' : '24px' }}>
                {articles.map((article) => (
                  <div key={article.id} className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-200 overflow-hidden">
                    <div style={{ padding: isMobile ? '16px' : '24px' }}>
                      <div className="flex justify-between items-start mb-3" style={{ gap: isMobile ? '8px' : '12px' }}>
                        <h3 className="font-semibold text-gray-900 line-clamp-2 break-words" style={{ fontSize: isMobile ? '15px' : '18px', flex: 1 }}>{article.title}</h3>
                        <span className={`rounded-full font-medium border ${getStatusColor(article.status)} flex-shrink-0`} style={{ padding: isMobile ? '4px 8px' : '4px 8px', fontSize: isMobile ? '10px' : '12px' }}>
                          {article.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-3 break-words" style={{ fontSize: isMobile ? '13px' : '14px' }}>{article.content}</p>
                      <div className="flex justify-between items-center text-gray-500" style={{ fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap', gap: isMobile ? '4px' : 0 }}>
                        <span>By {article.author}</span>
                        <span>{new Date(article.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && !loading && (
          <div style={{ gap: isMobile ? '16px' : '24px' }} className="space-y-6">
            <div className={`flex ${isMobile ? 'flex-col' : 'justify-between'} items-${isMobile ? 'start' : 'center'}`} style={{ gap: isMobile ? '12px' : 0 }}>
              <h2 className="font-bold text-gray-900" style={{ fontSize: isMobile ? '20px' : '24px' }}>Your Courses</h2>
              <button 
                onClick={() => navigate('/courses')}
                className="bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium"
                style={{ 
                  padding: isMobile ? '10px 16px' : '8px 16px',
                  fontSize: isMobile ? '14px' : '14px',
                  minHeight: isMobile ? '44px' : 'auto',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Browse Courses
              </button>
            </div>
            
            {courses.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg text-center border border-gray-200" style={{ padding: isMobile ? '32px 16px' : '48px' }}>
                <div className="bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" style={{ width: isMobile ? '64px' : '64px', height: isMobile ? '64px' : '64px' }}>
                  <span style={{ fontSize: isMobile ? '32px' : '32px' }}>🎓</span>
                </div>
                <h3 className="font-medium text-gray-900 mb-2" style={{ fontSize: isMobile ? '16px' : '18px' }}>No course enrollments</h3>
                <p className="text-gray-600 mb-6" style={{ fontSize: isMobile ? '13px' : '14px' }}>Enroll in courses to start your learning journey.</p>
                <button 
                  onClick={() => navigate('/courses')}
                  className="bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium"
                  style={{ 
                    padding: isMobile ? '12px 24px' : '12px 24px',
                    fontSize: isMobile ? '14px' : '14px',
                    minHeight: isMobile ? '44px' : 'auto'
                  }}
                >
                  Browse Available Courses
                </button>
              </div>
            ) : (
              <div style={{ gap: isMobile ? '12px' : '16px' }} className="space-y-4">
                {courses.map((enrollment) => (
                  <div key={enrollment.id} className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-200" style={{ padding: isMobile ? '16px' : '24px' }}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1" style={{ minWidth: 0 }}>
                        <h3 className="font-semibold text-gray-900 mb-2 break-words" style={{ fontSize: isMobile ? '16px' : '20px' }}>{enrollment.course.title}</h3>
                        <p className="text-gray-600 mb-3 break-words" style={{ fontSize: isMobile ? '13px' : '14px' }}>{enrollment.course.description}</p>
                        <div className="flex items-center text-gray-500" style={{ fontSize: isMobile ? '12px' : '14px', gap: isMobile ? '8px' : '24px', flexWrap: 'wrap' }}>
                          <span>Instructor: {enrollment.course.instructor}</span>
                          <span>Enrolled: {new Date(enrollment.enrolledAt).toLocaleDateString()}</span>
                          <span className={`rounded-full font-medium border ${getEnrollmentStatusColor(enrollment.enrollmentStatus)}`} style={{ padding: isMobile ? '4px 8px' : '4px 8px', fontSize: isMobile ? '10px' : '12px' }}>
                            {enrollment.enrollmentStatus}
                          </span>
                        </div>
                      </div>
                    </div>
                    {enrollment.progressPercentage !== undefined && (
                      <div style={{ marginTop: isMobile ? '12px' : '16px' }}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-700" style={{ fontSize: isMobile ? '12px' : '14px' }}>Progress</span>
                          <span className="font-medium text-gray-700" style={{ fontSize: isMobile ? '12px' : '14px' }}>{enrollment.progressPercentage?.toFixed(1) || 0}%</span>
                        </div>
                        <div className="bg-gray-200 rounded-full" style={{ height: isMobile ? '6px' : '12px' }}>
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                            style={{ width: `${enrollment.progressPercentage || 0}%`, height: isMobile ? '6px' : '12px' }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {enrollment.coursePaymentPlan === 'INSTALLMENT' &&
                      enrollment.installmentsTotal != null &&
                      enrollment.installmentsPaid != null &&
                      enrollment.installmentsPaid < enrollment.installmentsTotal && (
                        <div
                          className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900"
                          style={{ fontSize: isMobile ? '12px' : '13px' }}
                        >
                          <p className="font-medium mb-2">
                            Installment {enrollment.installmentsPaid} of {enrollment.installmentsTotal} paid — complete your plan to stay current.
                          </p>
                          <button
                            type="button"
                            onClick={() => handlePayNextCourseInstallment(enrollment.course.id)}
                            disabled={installmentPayingCourseId === enrollment.course.id}
                            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg px-4 py-2 disabled:opacity-50"
                          >
                            {installmentPayingCourseId === enrollment.course.id
                              ? 'Processing…'
                              : `Pay installment ${enrollment.installmentsPaid + 1} of ${enrollment.installmentsTotal}`}
                          </button>
                        </div>
                      )}
                    
                    {/* Action Buttons */}
                    <div className="mt-4 flex items-center" style={{ gap: isMobile ? '8px' : '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                      <button 
                        onClick={() => navigate(`/dashboard/courses/${enrollment.course.id}`)}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center"
                        style={{ 
                          padding: isMobile ? '12px 16px' : '12px 24px',
                          fontSize: isMobile ? '13px' : '14px',
                          minHeight: isMobile ? '44px' : 'auto',
                          flex: isMobile ? 1 : 1,
                          width: isMobile ? '100%' : 'auto',
                          transform: isMobile ? 'none' : 'hover:scale-105'
                        }}
                      >
                        <span>{enrollment.progressPercentage > 0 ? (isMobile ? '▶️ Continue' : '▶️ Continue Learning') : (isMobile ? '🚀 Start' : '🚀 Start Course')}</span>
                      </button>
                      <button 
                        onClick={() => navigate(`/dashboard/courses/${enrollment.course.id}`)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                        style={{ 
                          padding: isMobile ? '12px 16px' : '12px 16px',
                          fontSize: isMobile ? '13px' : '14px',
                          minHeight: isMobile ? '44px' : 'auto',
                          flex: isMobile ? 1 : 'none',
                          width: isMobile ? '100%' : 'auto'
                        }}
                      >
                        {isMobile ? '📄 Details' : '📄 View Details'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Question Packages Tab */}
        {activeTab === 'question-packages' && !loading && (
          <div style={{ gap: isMobile ? '16px' : '24px' }} className="space-y-6">
            <div className={`flex ${isMobile ? 'flex-col' : 'justify-between'} items-${isMobile ? 'start' : 'center'}`} style={{ gap: isMobile ? '12px' : 0 }}>
              <h2 className="font-bold text-gray-900" style={{ fontSize: isMobile ? '20px' : '24px' }}>{isMobile ? 'Your Questions' : 'Your Question Packages'}</h2>
              <button 
                onClick={() => navigate('/question-packages')}
                className="bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 font-medium"
                style={{ 
                  padding: isMobile ? '10px 16px' : '8px 16px',
                  fontSize: isMobile ? '14px' : '14px',
                  minHeight: isMobile ? '44px' : 'auto',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {isMobile ? 'Browse Packages' : 'Browse Question Packages'}
              </button>
            </div>
            
            {questionPackages.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg text-center border border-gray-200" style={{ padding: isMobile ? '32px 16px' : '48px' }}>
                <div className="bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" style={{ width: isMobile ? '64px' : '64px', height: isMobile ? '64px' : '64px' }}>
                  <span style={{ fontSize: isMobile ? '32px' : '32px' }}>❓</span>
                </div>
                <h3 className="font-medium text-gray-900 mb-2" style={{ fontSize: isMobile ? '16px' : '18px' }}>No question package enrollments</h3>
                <p className="text-gray-600 mb-6" style={{ fontSize: isMobile ? '13px' : '14px' }}>Enroll in question packages to start practicing.</p>
                <button 
                  onClick={() => navigate('/question-packages')}
                  className="bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 font-medium"
                  style={{ 
                    padding: isMobile ? '12px 24px' : '12px 24px',
                    fontSize: isMobile ? '14px' : '14px',
                    minHeight: isMobile ? '44px' : 'auto'
                  }}
                >
                  Browse Available Packages
                </button>
              </div>
            ) : (
              <div style={{ gap: isMobile ? '12px' : '16px' }} className="space-y-4">
                {questionPackages.map((enrollment) => (
                  <div key={enrollment.id} className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-200" style={{ padding: isMobile ? '16px' : '24px' }}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1" style={{ minWidth: 0 }}>
                        <h3 className="font-semibold text-gray-900 mb-2 break-words" style={{ fontSize: isMobile ? '16px' : '20px' }}>{enrollment.questionPackage.name}</h3>
                        <p className="text-gray-600 mb-3 break-words" style={{ fontSize: isMobile ? '13px' : '14px' }}>{enrollment.questionPackage.description}</p>
                        <div className="flex items-center text-gray-500" style={{ fontSize: isMobile ? '12px' : '14px', gap: isMobile ? '8px' : '24px', flexWrap: 'wrap' }}>
                          <span>Difficulty: {enrollment.questionPackage.difficultyLevel}</span>
                          <span>Enrolled: {new Date(enrollment.enrolledAt).toLocaleDateString()}</span>
                          <span className={`rounded-full font-medium border ${getEnrollmentStatusColor(enrollment.enrollmentStatus)}`} style={{ padding: isMobile ? '4px 8px' : '4px 8px', fontSize: isMobile ? '10px' : '12px' }}>
                            {enrollment.enrollmentStatus}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3" style={{ gap: isMobile ? '8px' : '16px', marginTop: isMobile ? '12px' : '16px' }}>
                      <div className="text-center bg-blue-50 rounded-lg" style={{ padding: isMobile ? '12px 8px' : '12px' }}>
                        <p className="font-bold text-blue-600" style={{ fontSize: isMobile ? '20px' : '24px' }}>{enrollment.questionsAttempted || 0}</p>
                        <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '14px' }}>Attempted</p>
                      </div>
                      <div className="text-center bg-green-50 rounded-lg" style={{ padding: isMobile ? '12px 8px' : '12px' }}>
                        <p className="font-bold text-green-600" style={{ fontSize: isMobile ? '20px' : '24px' }}>{enrollment.questionsCorrect || 0}</p>
                        <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '14px' }}>Correct</p>
                      </div>
                      <div className="text-center bg-purple-50 rounded-lg" style={{ padding: isMobile ? '12px 8px' : '12px' }}>
                        <p className="font-bold text-purple-600" style={{ fontSize: isMobile ? '20px' : '24px' }}>{enrollment.questionPackage.totalQuestions}</p>
                        <p className="text-gray-600" style={{ fontSize: isMobile ? '11px' : '14px' }}>Total</p>
                      </div>
                    </div>
                    {enrollment.questionsAttempted > 0 && (
                      <div style={{ marginTop: isMobile ? '12px' : '16px' }}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-700" style={{ fontSize: isMobile ? '12px' : '14px' }}>Progress</span>
                          <span className="font-medium text-gray-700" style={{ fontSize: isMobile ? '12px' : '14px' }}>{enrollment.progressPercentage || 0}%</span>
                        </div>
                        <div className="bg-gray-200 rounded-full" style={{ height: isMobile ? '6px' : '12px' }}>
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-300"
                            style={{ width: `${enrollment.progressPercentage || 0}%`, height: isMobile ? '6px' : '12px' }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {/* Accuracy Badge */}
                    {enrollment.questionsAttempted > 0 && (
                      <div style={{ marginTop: isMobile ? '12px' : '12px' }} className="flex items-center justify-center">
                        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-full" style={{ padding: isMobile ? '8px 12px' : '8px 16px' }}>
                          <span className="font-semibold text-purple-700" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                            🎯 Accuracy: {((enrollment.questionsCorrect / enrollment.questionsAttempted) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="mt-4 flex items-center" style={{ gap: isMobile ? '8px' : '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                      <button 
                        onClick={() => navigate(`/question-packages/${enrollment.questionPackage.id}`)}
                        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center"
                        style={{ 
                          padding: isMobile ? '12px 16px' : '12px 24px',
                          fontSize: isMobile ? '13px' : '14px',
                          minHeight: isMobile ? '44px' : 'auto',
                          flex: isMobile ? 1 : 1,
                          width: isMobile ? '100%' : 'auto',
                          transform: isMobile ? 'none' : 'hover:scale-105'
                        }}
                      >
                        <span>{enrollment.questionsAttempted > 0 ? (isMobile ? '▶️ Continue' : '▶️ Continue Practice') : (isMobile ? '🎯 Start' : '🎯 Start Practice')}</span>
                      </button>
                      <button 
                        onClick={() => navigate(`/question-packages/${enrollment.questionPackage.id}`)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                        style={{ 
                          padding: isMobile ? '12px 16px' : '12px 16px',
                          fontSize: isMobile ? '13px' : '14px',
                          minHeight: isMobile ? '44px' : 'auto',
                          flex: isMobile ? 1 : 'none',
                          width: isMobile ? '100%' : 'auto'
                        }}
                      >
                        {isMobile ? '📄 Details' : '📄 View Details'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Overlays for Innovative Features */}
      
      {/* Code Battle Modal */}
      {showCodeBattle && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex z-50" 
          style={{ alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? '0' : '16px' }}
          onClick={() => setShowCodeBattle(false)}
        >
          <div 
            className="bg-white shadow-2xl w-full overflow-y-auto"
            style={{ 
              borderRadius: isMobile ? '20px 20px 0 0' : '16px',
              maxWidth: isMobile ? '100%' : '768px',
              maxHeight: isMobile ? '90vh' : '90vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 flex justify-between items-center z-10" style={{ padding: isMobile ? '16px' : '16px' }}>
              <h2 className="font-bold text-gray-900 flex items-center" style={{ fontSize: isMobile ? '18px' : '24px' }}>
                <span style={{ marginRight: isMobile ? '8px' : '8px' }}>⚔️</span> Code Battle Royale
              </h2>
              <button 
                onClick={() => setShowCodeBattle(false)}
                className="text-gray-500 hover:text-gray-700 font-bold hover:bg-gray-100 rounded-lg transition-colors"
                style={{ 
                  fontSize: isMobile ? '20px' : '24px',
                  padding: isMobile ? '8px 12px' : '8px 12px',
                  minHeight: isMobile ? '44px' : 'auto'
                }}
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {user && <CodeBattle userId={user.id} />}
            </div>
          </div>
        </div>
      )}

      {/* Debugging Time Machine Modal */}
      {showTimeMachine && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTimeMachine(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <span className="mr-2">⏰</span> Debugging Time Machine
              </h2>
              <button 
                onClick={() => setShowTimeMachine(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {user && <DebuggingTimeMachine userId={user.id} />}
            </div>
          </div>
        </div>
      )}

      {/* Daily Challenge Modal */}
      {showDailyChallenge && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDailyChallenge(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <span className="mr-2">📅</span> Daily Challenge
              </h2>
              <button 
                onClick={() => setShowDailyChallenge(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {user && <DailyChallenges userId={user.id} />}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLeaderboard(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <span className="mr-2">🏆</span> Leaderboard
              </h2>
              <button 
                onClick={() => setShowLeaderboard(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {user && <Leaderboard userId={user.id} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
